package com.siriusxm.cmc.charts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import org.jetbrains.compose.web.css.CSSSizeValue

/**
 * Kotlin-friendly chart configuration builder.
 * Provides a DSL for configuring Chart.js charts.
 */
public class ChartDetails {
    public var type: ChartType = ChartType.LINE
    public var data: ChartDataDetails = ChartDataDetails()
    public var options: ChartOptionsDetails = ChartOptionsDetails()

    /**
     * Configure chart data including labels and datasets
     */
    public fun data(block: ChartDataDetails.() -> Unit) {
        data.apply(block)
    }

    /**
     * Configure chart options including scales, plugins, animations, etc.
     */
    public fun options(block: ChartOptionsDetails.() -> Unit) {
        options.apply(block)
    }

    /**
     * Convert to Chart.js ChartConfiguration object
     */
    internal fun toChartConfiguration(): ChartConfiguration = new {
        type = this@ChartDetails.type.value
        data = this@ChartDetails.data.toChartData()
        options = this@ChartDetails.options.toChartOptions()
    }
}

/**
 * Chart type enumeration
 */
public enum class ChartType(public val value: String) {
    LINE("line"),
    BAR("bar"),
    RADAR("radar"),
    DOUGHNUT("doughnut"),
    PIE("pie"),
    POLAR_AREA("polarArea"),
    BUBBLE("bubble"),
    SCATTER("scatter")
}

/**
 * Chart data configuration
 */
public class ChartDataDetails {
    public var labels: List<String> = emptyList()
    private val datasets = mutableListOf<DatasetDetails>()

    /**
     * Add a dataset to the chart
     */
    public fun dataset(block: DatasetDetails.() -> Unit) {
        datasets.add(DatasetDetails().apply(block))
    }

    /**
     * Add multiple datasets
     */
    public fun datasets(vararg blocks: DatasetDetails.() -> Unit) {
        blocks.forEach { dataset(it) }
    }

    internal fun toChartData(): ChartData = new {
        labels = this@ChartDataDetails.labels.toTypedArray()
        datasets = this@ChartDataDetails.datasets.map { it.toDataset() }.toTypedArray()
    }
}

/**
 * Dataset configuration
 */
public class DatasetDetails {
    public var label: String? = null
    public var data: List<Any> = emptyList() // Can be numbers, points, or other data types

    // Styling
    public var backgroundColor: ColorValue? = null
    public var borderColor: ColorValue? = null
    public var borderWidth: Number? = null
    public var borderRadius: Number? = null
    public var borderSkipped: String? = null

    // Line styling
    public var tension: Number? = null
    public var fill: Any? = null // boolean, string, or object
    public var stepped: Boolean? = null

    /**
     * Whether to draw a line across null data points. Chart.js defaults this to false, which
     * leaves a visible gap — the right behaviour when a null means "no value recorded". Only
     * set it true when the nulls are noise to be smoothed over rather than information.
     */
    public var spanGaps: Boolean? = null

    // Point styling
    public var pointRadius: Number? = null
    public var pointBackgroundColor: ColorValue? = null
    public var pointBorderColor: ColorValue? = null
    public var pointBorderWidth: Number? = null
    public var pointStyle: String? = null
    public var pointHoverRadius: Number? = null

    // Bar styling
    public var barThickness: Number? = null
    public var maxBarThickness: Number? = null
    public var minBarLength: Number? = null

    // Other
    public var hidden: Boolean? = null
    public var order: Number? = null
    public var stack: String? = null
    public var yAxisID: String? = null
    public var xAxisID: String? = null

    /**
     * Helper function to set data as numbers
     */
    public fun numbers(vararg values: Number) {
        data = values.toList()
    }

    /**
     * Helper function to set data as points
     */
    public fun points(block: PointListBuilder.() -> Unit) {
        data = PointListBuilder().apply(block).build()
    }

    internal fun toDataset(): Dataset = new {
        label = this@DatasetDetails.label

        val d = this@DatasetDetails.data
        // Convert data based on type
        data = when {
            d.isEmpty() -> emptyArray<Any>()
            d.first() is Number -> {
                // For line/bar charts with labels, pass numbers directly
                d.mapNotNull { it as? Number }.toTypedArray()
            }
            else -> {
                // Use point objects for scatter/bubble charts
                try {
                    @Suppress("UNCHECKED_CAST")
                    (d as List<DatasetPoint>).toTypedArray()
                } catch (_: Exception) {
                    emptyArray()
                }
            }
        }


        // Apply colors
        this@DatasetDetails.borderColor?.let { borderColor = it.toColorString() }
        this@DatasetDetails.backgroundColor?.let { color ->
            backgroundColor = when (color) {
                is SingleColor -> arrayOf(color.toColorString())
                is MultipleColors -> color.colors.toTypedArray()
            }
        }

        // Apply line/point styling
        this@DatasetDetails.borderWidth?.let { borderWidth = it }
        this@DatasetDetails.tension?.let { tension = it }
        this@DatasetDetails.spanGaps?.let { spanGaps = it }
        this@DatasetDetails.pointRadius?.let { pointRadius = it }
        this@DatasetDetails.pointHoverRadius?.let { pointHoverRadius = it }
    }
}

/**
 * Builder for point data
 */
public class PointListBuilder {
    private val points = mutableListOf<DatasetPoint>()

    /**
     * Add a point at label [x].
     *
     * A null [y] is a *gap*, not a zero: Chart.js skips the point and breaks the line there,
     * leaving the x label in place. Use it for "no value was recorded here", and keep
     * [DatasetDetails.spanGaps] false (the default) so the gap is not bridged. Passing 0.0
     * instead would draw a real data point at zero, which means something quite different.
     */
    public fun point(x: String, y: Number?) {
        points.add(new {
            this.x = x
            this.y = y
        })
    }

    /**
     * Add a point at numeric label [x], which is rendered as its string form.
     *
     * See the [String] overload for what a null [y] means.
     */
    public fun point(x: Number, y: Number?) {
        points.add(new {
            this.x = x.toString()
            this.y = y
        })
    }

    internal fun build(): List<DatasetPoint> = points
}

/**
 * Color value abstraction
 */
public sealed class ColorValue {
    public abstract fun toColorString(): String
}

public class SingleColor(public val color: String) : ColorValue() {
    public override fun toColorString(): String = color
}

public class MultipleColors(public val colors: List<String>) : ColorValue() {
    public override fun toColorString(): String = colors.firstOrNull() ?: ""
}

// Color helper functions
public fun rgb(r: Int, g: Int, b: Int): SingleColor = SingleColor("rgb($r, $g, $b)")
public fun rgba(r: Int, g: Int, b: Int, a: Double): SingleColor = SingleColor("rgba($r, $g, $b, $a)")
public fun hex(value: String): SingleColor = SingleColor(if (value.startsWith("#")) value else "#$value")
public fun colors(vararg values: String): MultipleColors = MultipleColors(values.toList())

/**
 * Chart options configuration
 */
public class ChartOptionsDetails {
    // Responsiveness
    public var responsive: Boolean = true
    public var maintainAspectRatio: Boolean = true
    public var aspectRatio: Number? = 2.0
    public var resizeDelay: Number? = null
    public var devicePixelRatio: Number? = null
    public var height: CSSSizeValue<*>? = null

    // Layout
    public var layout: LayoutDetails? = null

    // Interaction
    public var interaction: InteractionDetails? = null

    // Scales
    private val scales = mutableMapOf<String, ScaleDetails>()

    // Plugins
    public var plugins: PluginsDetails = PluginsDetails()

    // Animation
    public var animation: AnimationDetails? = null

    // Events
    public var events: List<String>? = null
    public var onHover: ((event: dynamic, elements: dynamic) -> Unit)? = null
    public var onClick: ((event: dynamic, elements: dynamic) -> Unit)? = null

    /**
     * Configure layout options
     */
    public fun layout(block: LayoutDetails.() -> Unit) {
        layout = LayoutDetails().apply(block)
    }

    /**
     * Configure interaction options
     */
    public fun interaction(block: InteractionDetails.() -> Unit) {
        interaction = InteractionDetails().apply(block)
    }

    /**
     * Configure scales
     */
    public fun scales(block: ScalesBuilder.() -> Unit) {
        ScalesBuilder(scales).apply(block)
    }

    /**
     * Configure plugins
     */
    public fun plugins(block: PluginsDetails.() -> Unit) {
        plugins.apply(block)
    }

    /**
     * Configure animation
     */
    public fun animation(block: AnimationDetails.() -> Unit) {
        animation = AnimationDetails().apply(block)
    }

    internal fun toChartOptions(): ChartOptions = new {
        responsive = this@ChartOptionsDetails.responsive
        if (height == null) {
            maintainAspectRatio = this@ChartOptionsDetails.maintainAspectRatio
            aspectRatio = this@ChartOptionsDetails.aspectRatio
        } else {
            maintainAspectRatio = false
        }

        this@ChartOptionsDetails.devicePixelRatio?.let { devicePixelRatio = it }

        if (this@ChartOptionsDetails.scales.isNotEmpty()) {
            scales = new {
                this@ChartOptionsDetails.scales.forEach { (key, scale) ->
                    when (key) {
                        "x" -> x = scale.toAxis()
                        "y" -> y = scale.toAxis()
                        // For multiple scales, we'd need to use dynamic access
                    }
                }
            }
        }

        plugins = this@ChartOptionsDetails.plugins.toPlugins()
    }
}

/**
 * Layout configuration
 */
public class LayoutDetails {
    public var padding: PaddingValue? = null
    public var autoPadding: Boolean = true

    public fun padding(all: Number) {
        padding = PaddingAll(all)
    }

    public fun padding(vertical: Number, horizontal: Number) {
        padding = PaddingSymmetric(vertical, horizontal)
    }

    public fun padding(top: Number, right: Number, bottom: Number, left: Number) {
        padding = PaddingDetailed(top, right, bottom, left)
    }
}

public sealed class PaddingValue
public class PaddingAll(public val value: Number) : PaddingValue()
public class PaddingSymmetric(public val vertical: Number, public val horizontal: Number) : PaddingValue()
public class PaddingDetailed(
    public val top: Number,
    public val right: Number,
    public val bottom: Number,
    public val left: Number
) : PaddingValue()

/**
 * Interaction configuration
 */
public class InteractionDetails {
    public var mode: String? = null // 'point', 'nearest', 'index', 'dataset', 'x', 'y'
    public var intersect: Boolean? = null
    public var axis: String? = null // 'x', 'y', 'xy', 'r'
    public var includeInvisible: Boolean? = null
}

/**
 * Scales builder
 */
public class ScalesBuilder(private val scales: MutableMap<String, ScaleDetails>) {
    /**
     * Configure the x-axis
     */
    public fun x(block: ScaleDetails.() -> Unit) {
        scales["x"] = ScaleDetails().apply(block)
    }

    /**
     * Configure the y-axis
     */
    public fun y(block: ScaleDetails.() -> Unit) {
        scales["y"] = ScaleDetails().apply(block)
    }

    /**
     * Configure a custom scale
     */
    public fun scale(id: String, block: ScaleDetails.() -> Unit) {
        scales[id] = ScaleDetails().apply(block)
    }
}

/**
 * Scale/Axis configuration
 */
public class ScaleDetails {
    public var type: ScaleType? = null
    public var display: Boolean = true
    public var position: String? = null // 'top', 'left', 'bottom', 'right'
    public var stacked: Boolean? = null
    public var weight: Number? = null
    public var min: Number? = null
    public var max: Number? = null
    public var suggestedMin: Number? = null
    public var suggestedMax: Number? = null
    public var beginAtZero: Boolean? = null
    public var grace: Any? = null // number or string percentage

    // Title
    public var title: ScaleTitleDetails? = null

    // Grid
    public var grid: GridDetails? = null

    // Ticks
    public var ticks: TicksDetails? = null

    // Time (for time scales)
    public var time: TimeDetails? = null

    /**
     * Configure scale title
     */
    public fun title(block: ScaleTitleDetails.() -> Unit) {
        title = ScaleTitleDetails().apply(block)
    }

    /**
     * Configure grid
     */
    public fun grid(block: GridDetails.() -> Unit) {
        grid = GridDetails().apply(block)
    }

    /**
     * Configure ticks
     */
    public fun ticks(block: TicksDetails.() -> Unit) {
        ticks = TicksDetails().apply(block)
    }

    /**
     * Configure time options (for time scales)
     */
    public fun time(block: TimeDetails.() -> Unit) {
        time = TimeDetails().apply(block)
    }

    internal fun toAxis(): Axis = new {
        this@ScaleDetails.type?.let { type = it.value }
        this@ScaleDetails.stacked?.let { stacked = it }
        this@ScaleDetails.title?.let {
            title = new {
                display = it.display
                text = it.text
            }
        }
        this@ScaleDetails.time?.let {
            time = new {
                unit = it.unit
            }
        }
    }
}

public enum class ScaleType(public val value: String) {
    LINEAR("linear"),
    LOGARITHMIC("logarithmic"),
    CATEGORY("category"),
    TIME("time"),
    TIME_SERIES("timeseries"),
    RADIAL_LINEAR("radialLinear")
}

public class ScaleTitleDetails {
    public var display: Boolean = false
    public var text: String = ""
    public var color: String? = null
    public var font: FontDetails? = null
    public var padding: Number? = null
    public var align: String? = null // 'start', 'center', 'end'
}

public class GridDetails {
    public var display: Boolean = true
    public var color: ColorValue? = null
    public var borderColor: ColorValue? = null
    public var borderWidth: Number? = null
    public var borderDash: List<Number>? = null
    public var borderDashOffset: Number? = null
    public var lineWidth: Number? = null
    public var drawOnChartArea: Boolean? = null
    public var drawTicks: Boolean? = null
    public var tickLength: Number? = null
    public var offset: Boolean? = null
    public var circular: Boolean? = null
}

public class TicksDetails {
    public var display: Boolean = true
    public var color: String? = null
    public var font: FontDetails? = null
    public var padding: Number? = null
    public var stepSize: Number? = null
    public var count: Number? = null
    public var precision: Number? = null
    public var maxTicksLimit: Number? = null
    public var maxRotation: Number? = null
    public var minRotation: Number? = null
    public var mirror: Boolean? = null
    public var autoSkip: Boolean? = null
    public var autoSkipPadding: Number? = null
    public var labelOffset: Number? = null
    public var showLabelBackdrop: Boolean? = null
    public var backdropColor: String? = null
    public var backdropPadding: Number? = null
    public var callback: ((value: Any, index: Int, ticks: Array<Any>) -> String)? = null
}

public class TimeDetails {
    public var unit: String? =
        null // 'millisecond', 'second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'
    public var round: String? = null
    public var isoWeekday: Boolean? = null
    public var displayFormats: Map<String, String>? = null
    public var tooltipFormat: String? = null
}

public class FontDetails {
    public var family: String? = null
    public var size: Number? = null
    public var style: String? = null // 'normal', 'italic', 'oblique'
    public var weight: String? = null // 'normal', 'bold', 'lighter', 'bolder', number
    public var lineHeight: Any? = null // number or string
}

/**
 * Plugins configuration
 */
public class PluginsDetails {
    public var legend: LegendDetails? = null
    public var tooltip: TooltipDetails? = null
    public var title: TitleDetails? = null
    public var subtitle: SubtitleDetails? = null
    public var filler: FillerDetails? = null
    public var decimation: DecimationDetails? = null

    /**
     * Configure legend
     */
    public fun legend(block: LegendDetails.() -> Unit) {
        legend = LegendDetails().apply(block)
    }

    /**
     * Configure tooltip
     */
    public fun tooltip(block: TooltipDetails.() -> Unit) {
        tooltip = TooltipDetails().apply(block)
    }

    /**
     * Configure title
     */
    public fun title(block: TitleDetails.() -> Unit) {
        title = TitleDetails().apply(block)
    }

    /**
     * Configure subtitle
     */
    public fun subtitle(block: SubtitleDetails.() -> Unit) {
        subtitle = SubtitleDetails().apply(block)
    }

    internal fun toPlugins(): Plugins = new {
        this@PluginsDetails.legend?.let {
            legend = new {
                display = it.display
            }
        }
    }
}

public class LegendDetails {
    public var display: Boolean = true
    public var position: String? = null // 'top', 'left', 'bottom', 'right', 'chartArea'
    public var align: String? = null // 'start', 'center', 'end'
    public var maxHeight: Number? = null
    public var maxWidth: Number? = null
    public var fullSize: Boolean? = null
    public var reverse: Boolean? = null
    public var labels: LegendLabelDetails? = null
    public var rtl: Boolean? = null
    public var textDirection: String? = null
    public var title: LegendTitleDetails? = null

    public fun labels(block: LegendLabelDetails.() -> Unit) {
        labels = LegendLabelDetails().apply(block)
    }

    public fun title(block: LegendTitleDetails.() -> Unit) {
        title = LegendTitleDetails().apply(block)
    }
}

public class LegendLabelDetails {
    public var boxWidth: Number? = null
    public var boxHeight: Number? = null
    public var color: String? = null
    public var font: FontDetails? = null
    public var padding: Number? = null
    public var generateLabels: ((chart: dynamic) -> Array<dynamic>)? = null
    public var filter: ((item: dynamic, data: dynamic) -> Boolean)? = null
    public var sort: ((a: dynamic, b: dynamic, data: dynamic) -> Number)? = null
    public var pointStyle: String? = null
    public var textAlign: String? = null
    public var usePointStyle: Boolean? = null
    public var pointStyleWidth: Number? = null
    public var useBorderRadius: Boolean? = null
    public var borderRadius: Number? = null
}

public class LegendTitleDetails {
    public var display: Boolean = false
    public var text: String = ""
    public var color: String? = null
    public var font: FontDetails? = null
    public var padding: Number? = null
}

public class TooltipDetails {
    public var enabled: Boolean = true
    public var external: ((context: dynamic) -> Unit)? = null
    public var mode: String? = null
    public var intersect: Boolean? = null
    public var position: String? = null
    public var backgroundColor: String? = null
    public var titleColor: String? = null
    public var titleFont: FontDetails? = null
    public var titleAlign: String? = null
    public var titleSpacing: Number? = null
    public var titleMarginBottom: Number? = null
    public var bodyColor: String? = null
    public var bodyFont: FontDetails? = null
    public var bodyAlign: String? = null
    public var bodySpacing: Number? = null
    public var footerColor: String? = null
    public var footerFont: FontDetails? = null
    public var footerAlign: String? = null
    public var footerSpacing: Number? = null
    public var footerMarginTop: Number? = null
    public var padding: Number? = null
    public var caretPadding: Number? = null
    public var caretSize: Number? = null
    public var cornerRadius: Number? = null
    public var multiKeyBackground: String? = null
    public var displayColors: Boolean? = null
    public var boxWidth: Number? = null
    public var boxHeight: Number? = null
    public var boxPadding: Number? = null
    public var usePointStyle: Boolean? = null
    public var borderColor: String? = null
    public var borderWidth: Number? = null
    public var rtl: Boolean? = null
    public var textDirection: String? = null
    public var animation: AnimationDetails? = null
}

public class TitleDetails {
    public var display: Boolean = false
    public var text: Any? = null // string or string[]
    public var color: String? = null
    public var font: FontDetails? = null
    public var padding: Number? = null
    public var align: String? = null
    public var position: String? = null
    public var fullSize: Boolean? = null
}

public class SubtitleDetails {
    public var display: Boolean = false
    public var text: Any? = null // string or string[]
    public var color: String? = null
    public var font: FontDetails? = null
    public var padding: Number? = null
    public var align: String? = null
    public var position: String? = null
    public var fullSize: Boolean? = null
}

public class FillerDetails {
    public var propagate: Boolean? = null
    public var drawTime: String? = null // 'beforeDatasetDraw', 'beforeDatasetsDraw'
}

public class DecimationDetails {
    public var enabled: Boolean = false
    public var algorithm: String? = null // 'min-max', 'lttb'
    public var samples: Number? = null
    public var threshold: Number? = null
}

/**
 * Animation configuration
 */
public class AnimationDetails {
    public var duration: Number = 1000
    public var easing: String = "easeOutQuart"
    public var delay: Number? = null
    public var loop: Boolean? = null

    // Animation callbacks
    public var onProgress: ((animation: dynamic) -> Unit)? = null
    public var onComplete: ((animation: dynamic) -> Unit)? = null
}

/**
 * DSL function to create ChartDetails
 */
public fun ChartDetails(block: ChartDetails.() -> Unit): ChartDetails {
    return ChartDetails().apply(block)
}

/**
 * Chart composable that accepts Kotlin-friendly ChartDetails
 */
@Composable
public fun Chart(
    details: ChartDetails,
    key: Any? = null
) {
    // Convert to configuration inside a remember to avoid recreating on every recomposition
    val configuration by remember(details.type, details.data, details.options) {
        mutableStateOf(details.toChartConfiguration())
    }

    ChartJs(
        configuration = configuration,
        height = details.options.height,
        key = key
    )
}